import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Navigation, Truck, Wallet, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../lib/api";
import { openMap } from "../../lib/openMap";
import CallButton from "../../components/CallButton";
import LseDeliverSheet, { LseRiderPlan } from "../../components/lse/LseDeliverSheet";

/**
 * Rider Order Detail. Single screen with full info + the one big action
 * button driving the rider through PICKED_UP → OUT_FOR_DELIVERY → DELIVERED.
 */
export default function RiderOrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [o, setO] = useState(null);
    const [busy, setBusy] = useState(false);
    // Location Service Engine: orders stamped with delivery proof are completed through the proof sheet.
    const [proofOpen, setProofOpen] = useState(false);
    const needsProof = !!(o?.delivery_proof && !o.delivery_proof.verified_at);

    const load = async () => {
        try { const { data } = await api.get(`/rider/orders/${id}`); setO(data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await load(); })();
        return () => { cancelled = true; };
    }, [id]);

    // Push rider GPS every 30s while the order is being actively
    // delivered (ASSIGNED / PICKED_UP / OUT_FOR_DELIVERY). Uses the
    // PARALLEL rider_status so we don't depend on the shop's prep state.
    const active = o && ["ASSIGNED", "PICKED_UP", "OUT_FOR_DELIVERY"].includes(o.rider_status || "") && o.rider_id;
    useEffect(() => {
        if (!active || !navigator.geolocation) return;
        let cancelled = false;
        const push = () => {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    if (cancelled) return;
                    try {
                        await api.post(`/rider/location?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
                    } catch (err) { console.debug("[Rider] location ping failed (non-fatal):", err); }
                },
                () => { /* permission denied / unavailable — silent */ },
                { enableHighAccuracy: true, maximumAge: 15000, timeout: 8000 },
            );
        };
        push();
        const t = setInterval(push, 30000);
        return () => { cancelled = true; clearInterval(t); };
    }, [active]);

    const action = async () => {
        if (!o) return;
        setBusy(true);
        try {
            const rs = o.rider_status;
            if (!o.rider_id) {
                await api.post(`/rider/orders/${id}/accept`);
                toast.success("Order accepted");
            } else if (rs === "ASSIGNED") {
                await api.patch(`/rider/orders/${id}/rider-status?status=PICKED_UP`);
                toast.success("Picked up");
            } else if (rs === "PICKED_UP") {
                await api.patch(`/rider/orders/${id}/rider-status?status=OUT_FOR_DELIVERY`);
                toast.success("On the way");
            } else if (rs === "OUT_FOR_DELIVERY" && needsProof) {
                setProofOpen(true);
                return;
            } else if (rs === "OUT_FOR_DELIVERY") {
                // Final tap routes by payment method:
                //  - COD → cash collected confirmation
                //  - everything else → plain delivered
                if ((o.payment_method || "").toUpperCase() === "COD" && !o.cdc_enabled) {
                    await api.post(`/rider/orders/${id}/cod-collect`);
                    toast.success("Cash collected · delivered ✨");
                } else {
                    await api.patch(`/rider/orders/${id}/rider-status?status=DELIVERED`);
                    toast.success("Delivered ✨");
                }
            }
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const cdcMarkPaid = async () => {
        if (!o) return;
        setBusy(true);
        try {
            await api.post(`/rider/orders/${id}/cdc-paid`);
            toast.success("Marked paid to shop — added to your earnings");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    if (!o) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>;

    const adr = o.delivery_address || {};
    // iter134h — pass structured location objects to ``openMap`` so it
    // can build the correct platform URI scheme (``geo:`` on Android,
    // ``maps://`` on iOS). See ``lib/openMap.js``.
    // iter134e3 — ``kind`` makes openMap enforce lat/lng: if the
    // order was saved without pickup/drop coordinates, the user sees
    // "Pickup/Delivery location not provided" instead of the app
    // opening a wrong place matching the restaurant/customer NAME.
    const pickup = {
        lat: o.restaurant_location?.lat,
        lng: o.restaurant_location?.lng,
        label: o.restaurant_name || "",
        kind: "pickup",
    };
    const drop = {
        lat: adr.lat,
        lng: adr.lng,
        label: [adr.line1, adr.city].filter(Boolean).join(", "),
        address: [adr.line1, adr.city].filter(Boolean).join(", "),
        kind: "drop",
    };
    const cdc = !!o.cdc_enabled;
    const totalQty = (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0);

    const actionLabel = (() => {
        if (!o.rider_id) return "Accept order";
        const rs = o.rider_status;
        if (rs === "ASSIGNED") return "Picked up at shop";
        if (rs === "PICKED_UP") return "Start delivery";
        if (rs === "OUT_FOR_DELIVERY") {
            if (needsProof) return "Complete delivery";
            if ((o.payment_method || "").toUpperCase() === "COD" && !o.cdc_enabled) {
                return `Confirm cash collected (${inr(o.totals?.grand_total)})`;
            }
            return "Mark delivered";
        }
        return null;
    })();
    const showCdcPaidBtn = cdc && o.rider_id && !o.cdc_paid_by_rider && ["ASSIGNED", "PICKED_UP"].includes(o.rider_status || "");

    return (
        <div className="bizil-rider max-w-md mx-auto pb-32">
            <header className="px-4 py-4 flex items-center gap-3 bg-card border-b border-border sticky top-0 z-10">
                <button onClick={() => nav(-1)} className="p-1 -ml-1"><ArrowLeft size={22} /></button>
                <div className="flex-1">
                    <p className="font-bold text-sm">{o.order_unique_id}</p>
                    <p className="text-xs text-muted-foreground">
                        Shop: {o.status} · Rider: {o.rider_status || "Unassigned"} · {o.payment_method}
                    </p>
                </div>
            </header>

            <div className="p-4 space-y-3">
                {/* Location Service Engine: delivery date / slot / village route (renders nothing otherwise). */}
                <LseRiderPlan order={o} />
                {/* Pickup */}
                <Card title="Pickup" tone="sky" icon={<MapPin size={14} />}>
                    <p className="font-semibold text-sm">{o.restaurant_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{o.zone_name ? `Zone · ${o.zone_name}` : ""}</p>
                    <ActionRow phone={o.restaurant_phone} mapTarget={pickup} />
                </Card>

                {/* Drop */}
                <Card title="Drop-off" tone="violet" icon={<MapPin size={14} />}>
                    <p className="font-semibold text-sm">{o.customer_name || "Customer"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3">{[adr.line1, adr.line2, adr.city, adr.pincode].filter(Boolean).join(", ")}</p>
                    <ActionRow phone={o.customer_mobile} mapTarget={drop} />
                </Card>

                {/* Settlement */}
                {cdc ? (
                    <Card title="Cash-During-Collection" tone="violet" icon={<Truck size={14} />}>
                        <p className="text-sm"><strong>Pay restaurant {inr(o.totals?.merchant_payout)}</strong> on pickup, then collect <strong>{inr(o.totals?.grand_total)}</strong> from customer.</p>
                        {o.cdc_paid_by_rider ? (
                            <p className="mt-2 text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                                <CheckCircle2 size={12} /> Paid {inr(o.cdc_paid_amount)} to shop — added to your earnings
                            </p>
                        ) : showCdcPaidBtn ? (
                            <button
                                data-testid="cdc-paid-btn"
                                disabled={busy}
                                onClick={cdcMarkPaid}
                                className="mt-3 w-full bg-violet-600 text-white rounded-xl py-2.5 font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                            >
                                <Truck size={14} /> I paid {inr(o.totals?.merchant_payout)} to shop
                            </button>
                        ) : null}
                    </Card>
                ) : o.payment_method === "COD" ? (
                    <Card title="Cash on Delivery" tone="amber" icon={<Wallet size={14} />}>
                        <p className="text-sm"><strong>Collect {inr(o.totals?.grand_total)}</strong> from the customer at delivery.</p>
                    </Card>
                ) : (
                    <Card title="Prepaid" tone="emerald" icon={<Wallet size={14} />}>
                        <p className="text-sm">No cash handling. Just deliver.</p>
                    </Card>
                )}

                {/* Items */}
                <Card title={`Items (${totalQty})`}>
                    <ul className="divide-y divide-border">
                        {(o.items || []).map((it) => (
                            <li key={it.product_id + (it.variant_id || "")} className="flex items-center gap-2 py-2 text-sm">
                                <span className={`food-mark ${it.food_type === "NON_VEG" ? "non-veg" : "veg"}`} />
                                <span className="flex-1">{it.name}</span>
                                <span className="text-muted-foreground">×{it.quantity}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            </div>

            {actionLabel && (
                <div className="bizil-rider-fixed fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40 bg-card border-t border-border px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                    <button data-testid="rider-next-action" disabled={busy} onClick={action} className="w-full bg-foreground text-background rounded-2xl py-4 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {actionLabel}
                    </button>
                </div>
            )}
            {proofOpen && (
                <LseDeliverSheet order={o} onClose={() => setProofOpen(false)} onDone={() => { setProofOpen(false); load(); }} />
            )}
        </div>
    );
}

function Card({ title, children, icon, tone }) {
    const tones = { sky: "bg-sky-50 border-sky-200", violet: "bg-violet-50 border-violet-200", amber: "bg-amber-50 border-amber-200", emerald: "bg-emerald-50 border-emerald-200" };
    const cls = tone ? tones[tone] : "bg-card border-border";
    return (
        <div className={`border rounded-2xl p-3 ${cls}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1 inline-flex items-center gap-1">{icon}{title}</p>
            {children}
        </div>
    );
}

function ActionRow({ phone, mapTarget }) {
    // iter134h — mapTarget is a { lat, lng, label, address, kind }
    // object; ``openMap`` picks the right URI scheme per platform.
    // iter134e3 — always render the Navigate button when a
    // ``kind`` is set (rider pickup/drop) even if coords are
    // missing. openMap will show a "location not provided" toast
    // when tapped so the rider gets actionable feedback instead of
    // an inert-looking button.
    const showNav = mapTarget && (
        mapTarget.kind || mapTarget.lat || mapTarget.label || mapTarget.address
    );
    return (
        <div className="flex items-center gap-2 mt-2">
            {phone && <CallButton phone={phone} className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl py-2 text-xs font-bold"><Phone size={13} /> Call</CallButton>}
            {showNav && <button
                type="button"
                data-testid="rider-order-navigate"
                onClick={() => openMap(mapTarget)}
                className="flex-1 inline-flex items-center justify-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl py-2 text-xs font-bold"
            ><Navigation size={13} /> Navigate</button>}
        </div>
    );
}
